import { useRef, useState } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { HrSystemConflictCard } from '@lp/features/Scheduler/components';
import { CCShiftCellDropdown } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/components';
import {
  CC_SHIFT_OPTION_DROPDOWN_LIST,
  ccShiftOptionTypeNames,
} from '@lp/features/Scheduler/SchedulerClinicalCoordinators/constants';
import {
  getShiftOptionFromShift,
  getShiftOptionIconColor,
} from '@lp/features/Scheduler/SchedulerClinicalCoordinators/utils';
import {
  CCResolveHrSystemConflict,
  CCResourceCCShiftTypeHrSystemConflict,
  CCShift,
  CCShiftSelectPayload,
  ScheduleHrSystemConflictType,
} from '@/api/apis/resources/schedule';
import { shiftTypeNames } from '@/constants/schedule.constants';

type CCHrSystemConflictCardProps = {
  conflict: CCResourceCCShiftTypeHrSystemConflict;
  onConflictResolve: (resolveData: CCResolveHrSystemConflict) => void;
};

export const CCHrSystemConflictCard = ({ conflict, onConflictResolve }: CCHrSystemConflictCardProps) => {
  const { conflictId, type, hrSystemShiftType } = conflict;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [shiftPayload, setShiftPayload] = useState<CCShiftSelectPayload>();
  const shiftOption = getShiftOptionFromShift(shiftPayload as CCShift);
  const textColor = getShiftOptionIconColor(shiftOption);
  const isRemoved = type === ScheduleHrSystemConflictType.HrSystemShiftTypeRemoved;

  const onApply = () => {
    const setShiftData = isRemoved ? shiftPayload : { shiftType: hrSystemShiftType, isManager: null };
    if (setShiftData) onConflictResolve({ conflictId, setShiftData });
  };

  return (
    <HrSystemConflictCard conflict={conflict} isApplyDisabled={isRemoved && !shiftPayload} onApply={onApply}>
      {isRemoved ? (
        <>
          <button
            ref={triggerRef}
            className={cn('flex text-m-r hover:underline', shiftOption ? textColor : 'text-base-25')}
            onClick={toggleIsOpen}
          >
            <span>{shiftOption ? ccShiftOptionTypeNames[shiftOption] : 'No shift'}</span>
            <MdOutlineKeyboardArrowDown size={18} className={cn({ 'rotate-180': isOpen })} />
          </button>
          {isOpen && (
            <CCShiftCellDropdown
              triggerRef={triggerRef}
              title="Pick Shift"
              isPlacementBottom
              isOpen={isOpen}
              onOpenChange={toggleIsOpen}
              shiftData={shiftPayload as CCShift} // Can omit other fields in this case
              onShiftChange={setShiftPayload}
              optionList={CC_SHIFT_OPTION_DROPDOWN_LIST}
              canReset
            />
          )}
        </>
      ) : (
        <span>{shiftTypeNames[hrSystemShiftType]}</span>
      )}
    </HrSystemConflictCard>
  );
};
